import { createResultBuffer, createParamsBuffer, stageReadback, destroyBuffers } from "../util/buffer.mjs";
import { createBindGroup } from "../util/bindgroup.mjs";
import { runComputePass, submit } from "../util/compute.mjs";
import { extractResult } from "../util/result.mjs";
import { getPipeline } from "../util/pipeline.mjs";
import { calcWorkgroups } from "../util/workgroup.mjs";
import { splitDD } from "../util/f64.mjs";
import { FORMULAS } from "./formulas.mjs";

/**
 * Packs one formula's uniform fields in `Params`-struct declaration order.
 * `precision: "f64"` formulas (see formulas.mjs) get center_re/center_im/
 * scale each split into a (hi, lo) f32 pair instead of a single f32 — the
 * only shape difference; everything else about rendering them is identical.
 * @param {import("./formulas.mjs").FORMULAS[string]} formula
 * @param {{ width: number, height: number, centerRe: number, centerIm: number, scale: number, maxIter: number, extra: Record<string, number> }} view
 * @returns {{ value: number, type: "f32"|"u32"|"i32" }[]}
 */
function buildParams(formula, view) {
  const { width, height, centerRe, centerIm, scale, maxIter, extra } = view;
  const extraFields = formula.extraParams.map((p) => ({
    value: extra[p.name] ?? p.default,
    type: p.type,
  }));

  if (formula.precision === "f64") {
    const cRe = splitDD(centerRe);
    const cIm = splitDD(centerIm);
    const s = splitDD(scale);
    return [
      { value: width, type: "u32" },
      { value: height, type: "u32" },
      { value: cRe.hi, type: "f32" },
      { value: cRe.lo, type: "f32" },
      { value: cIm.hi, type: "f32" },
      { value: cIm.lo, type: "f32" },
      { value: s.hi, type: "f32" },
      { value: s.lo, type: "f32" },
      { value: maxIter, type: "u32" },
      ...extraFields,
    ];
  }

  return [
    { value: width, type: "u32" },
    { value: height, type: "u32" },
    { value: centerRe, type: "f32" },
    { value: centerIm, type: "f32" },
    { value: scale, type: "f32" },
    { value: maxIter, type: "u32" },
    ...extraFields,
  ];
}

/**
 * Renders one frame of a complex-plane fractal and returns its packed pixels.
 *
 * This is the *only* render routine in the app — every formula in FORMULAS
 * goes through it. Only two things change between fractals: which compiled
 * pipeline runs, and which extra fields get appended to the params buffer.
 * The dispatch shape (one 8×8 workgroup per pixel tile, one bind group, one
 * pass) is identical for all of them, which is what makes switching fractals
 * at runtime a pipeline-cache lookup rather than a different code path.
 *
 * Flow: compile → upload → bind → dispatch → readback → destroy.
 *
 * @param {GPUDevice} device
 * @param {string} formulaName - key into FORMULAS
 * @param {{
 *   width: number, height: number,
 *   centerRe: number, centerIm: number, scale: number,
 *   maxIter: number,
 *   extra?: Record<string, number>,
 * }} view
 * @returns {Promise<Uint32Array>} packed 0xAABBGGRR pixels, length `width * height`
 * @throws {Error} if `formulaName` is unknown or `width`/`height` are non-positive
 */
export async function renderFractal(device, formulaName, view) {
  const formula = FORMULAS[formulaName];
  if (!formula) throw new Error(`Unknown fractal "${formulaName}".`);

  const { width, height, centerRe, centerIm, scale, maxIter, extra = {} } = view;
  if (width <= 0 || height <= 0) {
    throw new Error("width and height must be positive.");
  }

  const pipeline = await getPipeline(device, formula.shader);

  const params = buildParams(formula, { width, height, centerRe, centerIm, scale, maxIter, extra });

  const outputBuffer = createResultBuffer(width * height * 4, `${formulaName}-output`);
  const paramsBuffer = createParamsBuffer(params, `${formulaName}-params`);

  try {
    const bindGroup = createBindGroup(pipeline.getBindGroupLayout(0), [outputBuffer, paramsBuffer]);
    const { commandEncoder } = runComputePass(pipeline, bindGroup, calcWorkgroups(height, width));
    const readBuffer = stageReadback(commandEncoder, outputBuffer);

    submit(commandEncoder);

    return await extractResult(readBuffer, Uint32Array);
  } finally {
    destroyBuffers(outputBuffer, paramsBuffer);
  }
}
